import React, { useEffect, useState } from 'react'
import { Button, ConfigProvider, Form, Input, Modal, Radio, Select, Tree } from 'antd'
import { useTranslation } from 'react-i18next'
import { FormButton, TreeWrapper } from '../group.styled'
import { useSelector } from 'react-redux'
import { addRole, addRolePermission, modifyRole, modifyRolePermission } from '../../../apis/role'

function RoleModal (props) {
  const { show, setShow, data, successCallback = () => {}, adminMode, groupsOption, allPermissionMenu } = props
  const { t: ln_t } = useTranslation()
  const [form] = Form.useForm()
  const [isLoading, setIsLoading] = useState(false)
  const [treeData, setTreeData] = useState([])
  const [permission, setPermission] = useState([])

  const user = useSelector((state) => state.user)

  const defPlaceholder = (name, type) => {
    const message = type === 'input' ? ln_t('util.pleaseEnter') : type === 'select' ? ln_t('util.pelaseSelect') : ''
    return `${message} ${ln_t(`group.${name}`)}`
  }
  const defConfig = (name, type, required = true) => ({ name, label: ln_t(`group.${name}`), rules: [{ required, message: defPlaceholder(name, type) }] })

  const handleClose = () => {
    setShow(false)
    setPermission()
    form.resetFields()
  }

  const handleAdd = async (reqBody, functionIds) => {
    setIsLoading(true)
    const role = await addRole(reqBody)
    if (role?.status !== 201) return setIsLoading(false)
    const addPermission = await addRolePermission({ roleId: role?.data?.id, functionIds })
    if (addPermission?.status === 200) {
      successCallback()
      handleClose()
    }
    setIsLoading(false)
  }
  const handleMod = async (reqBody, functionIds) => {
    setIsLoading(true)
    const res = await modifyRole(data?.id, reqBody)
    if (res?.status !== 200) return setIsLoading(false)
    const modPermission = await modifyRolePermission({ roleId: data?.id, functionIds })
    if (modPermission?.status === 200) {
      successCallback()
      handleClose()
    }
    setIsLoading(false)
  }
  const handleSave = async value => {
    const { permission, groupId, ...other } = value || {}
    const reqBody = { ...other, groupId: adminMode ? groupId : user?.nowGroup }
    const functionIds = permission?.map(i => i?.split('-')?.slice(-1))
    const addMode = !data || data?.addMode
    addMode ? handleAdd(reqBody, functionIds) : handleMod(reqBody, functionIds)
  }

  const constructTree = data => {
    const parent = data?.filter(i => i.parentId === 0)
    return parent?.map(i => ({
      title: ln_t(`sidebar.${i.key}`),
      key: `0-${i.id}`,
      keyFromApi: i.key,
      children: data?.filter(j => j?.parentId === i.id)?.map(j => ({
        title: ln_t(`sidebar.${j.key}`),
        key: `0-${j.id}`
      }))
    }))
  }

  const handleChangeGroup = e => {
    const allowed = groupsOption?.find(i => i.value === e)?.functions?.split(',') || []
    const menu = allPermissionMenu?.filter(i => allowed.includes(`${i.id}`)) || []
    const menuKeys = menu?.map(i => i.id)
    const neededParents = [...new Set(menu?.filter(i => i.parentId !== 0 && !menuKeys.includes(i.parentId))?.map(i => i.parentId) || [])]
    if (neededParents.length) neededParents?.forEach(i => menu.push(allPermissionMenu.find(j => j.id === i)))
    const tree = constructTree(menu)
    setTreeData(tree)
    return tree
  }

  useEffect(() => {
    if (!show) return

    if (data?.addMode) {
      setTreeData(constructTree(adminMode ? [] : user?.nowGroupFunctionsDetail))
      return form.resetFields()
    }
    setTreeData(constructTree(adminMode ? [] : user?.nowGroupFunctionsDetail))

    const { name, status, functions, groupId } = data
    const funcKeys = functions?.map(i => `0-${i.id}`)
    let removeSettingId
    if (adminMode) {
      const tree = handleChangeGroup(groupId)
      const settingObj = tree?.find(i => i.keyFromApi === 'management')
      const settingChildrenIds = settingObj?.children?.map(i => i.key)
      const anyMissingChildren = settingChildrenIds?.some(i => !funcKeys.includes(i))
      removeSettingId = anyMissingChildren ? settingObj?.key : false
    }
    const func = funcKeys?.filter(i => removeSettingId ? i !== removeSettingId : true)
    setPermission(func)
    form.setFieldsValue({ name, status, permission: func, groupId })
  }, [show])

  const permissionWarning = adminMode && !treeData.length ? { validateStatus: 'warning', help: ln_t('message.info.selectGroupFirst') } : {}
  return (
    <ConfigProvider theme={{ token: { colorPrimary: 'rgb(71, 182, 175)', colorPrimaryHover: 'rgba(71, 182, 175,.8)' } }}>
      <Modal title={`${ln_t(`util.${data && !data?.addMode ? 'edit' : 'add'}`)} ${ln_t('group.role')}`} open={show} onCancel={handleClose} footer={null} style={{ top: '32px' }}>
        <Form form={form} layout="vertical" requiredMark={false} onFinish={e => handleSave(e)}>
          <Form.Item {...defConfig('name', 'input')}>
            <Input placeholder={defPlaceholder('name', 'input')} />
          </Form.Item>
          {adminMode
            ? <Form.Item {...defConfig('group', 'select')} name="groupId">
                <Select
                  showSearch
                  disabled={!data?.addMode}
                  placeholder={defPlaceholder('group', 'select')}
                  options={groupsOption}
                  onChange={e => handleChangeGroup(e)}
                  optionFilterProp="children"/>
              </Form.Item>
            : ''}
          <TreeWrapper>
            <Form.Item {...defConfig('permission', 'input')} {...permissionWarning}>
              {treeData.length > 0 && <Tree
                checkable
                defaultExpandAll
                checkedKeys={permission}
                treeData={treeData}
                onCheck={e => { form.setFieldValue('permission', e); setPermission(e) }}
              />}
            </Form.Item>
          </TreeWrapper>
          <Form.Item {...defConfig('status', 'select')} initialValue={1}>
            <Radio.Group
              options={[{ value: 1, label: ln_t('util.enable') }, { value: 0, label: ln_t('util.disable') }]}
              optionType="button"
              buttonStyle="solid"
            />
          </Form.Item>
          <FormButton>
            <Button type="default" onClick={handleClose}>{ln_t('util.cancel')}</Button>
            <Button type="primary" htmlType="submit" loading={isLoading}>{ln_t('util.confirm')}</Button>
          </FormButton>
        </Form>
      </Modal>
    </ConfigProvider>
  )
}

export default RoleModal
